/**
 * SettingInput - Renders the appropriate input control based on setting value_type
 */
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Toggle } from "@/components/base/toggle/toggle";
import { TextArea } from "@/components/base/textarea/textarea";
import { SecretInput } from "./secret-input";
import type { Setting } from "@/types/api/settings";

interface SettingInputProps {
    setting: Setting;
    value: unknown;
    onChange: (value: unknown) => void;
    validationError?: string;
}

export function SettingInput({ setting, value, onChange, validationError }: SettingInputProps) {
    const { value_type, validation_rules, is_secret, is_editable, display_name } = setting;

    // Handle secret fields
    if (is_secret) {
        return (
            <SecretInput
                value={String(value ?? "")}
                onChange={(v) => onChange(v)}
                isDisabled={!is_editable}
                placeholder={`Enter ${display_name.toLowerCase()}`}
                aria-label={display_name}
            />
        );
    }

    // Handle boolean toggle
    if (value_type === "boolean") {
        return (
            <Toggle
                isSelected={Boolean(value)}
                onChange={(selected) => onChange(selected)}
                isDisabled={!is_editable}
                aria-label={display_name}
                size="sm"
            />
        );
    }

    // Handle string with options (dropdown)
    if (value_type === "string" && validation_rules?.options && validation_rules.options.length > 0) {
        const options = validation_rules.options.map((opt) => ({
            id: opt,
            label: opt,
        }));

        return (
            <Select
                items={options}
                selectedKey={String(value ?? "")}
                onSelectionChange={(key) => onChange(key)}
                isDisabled={!is_editable}
                aria-label={display_name}
                size="sm"
                className="w-full"
            >
                {(item) => (
                    <Select.Item key={item.id} id={item.id}>
                        {item.label}
                    </Select.Item>
                )}
            </Select>
        );
    }

    // Handle integer
    if (value_type === "integer") {
        return (
            <Input
                type="number"
                value={String(value ?? "")}
                onChange={(v) => {
                    const num = parseInt(v, 10);
                    onChange(isNaN(num) ? null : num);
                }}
                isDisabled={!is_editable}
                isInvalid={!!validationError}
                hint={validationError}
                aria-label={display_name}
                size="sm"
                inputClassName="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
        );
    }

    // Handle float
    if (value_type === "float") {
        return (
            <Input
                type="number"
                value={String(value ?? "")}
                onChange={(v) => {
                    const num = parseFloat(v);
                    onChange(isNaN(num) ? null : num);
                }}
                isDisabled={!is_editable}
                isInvalid={!!validationError}
                hint={validationError}
                aria-label={display_name}
                size="sm"
                inputClassName="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
        );
    }

    // Handle JSON or list (textarea)
    if (value_type === "json" || value_type === "list") {
        const stringValue = typeof value === "string" ? value : JSON.stringify(value, null, 2);
        return (
            <TextArea
                value={stringValue}
                onChange={(v) => {
                    try {
                        const parsed = JSON.parse(v);
                        onChange(parsed);
                    } catch {
                        // Keep as string if not valid JSON
                        onChange(v);
                    }
                }}
                isDisabled={!is_editable}
                isInvalid={!!validationError}
                hint={validationError}
                aria-label={display_name}
                rows={4}
                textAreaClassName="font-mono text-sm"
            />
        );
    }

    // Default: string input
    return (
        <Input
            type="text"
            value={String(value ?? "")}
            onChange={(v) => onChange(v)}
            isDisabled={!is_editable}
            isInvalid={!!validationError}
            hint={validationError}
            aria-label={display_name}
            size="sm"
        />
    );
}
